import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import { initializeAuth } from '../../store/authSlice';
import OnboardingGuard from './OnboardingGuard';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireOnboardingComplete?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAuth = true,
  requireOnboardingComplete = true,
}) => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading, isInitialized } = useAppSelector((state) => state.auth);
  const location = useLocation();

  // Initialize auth state on app load
  useEffect(() => {
    if (!isInitialized) {
      dispatch(initializeAuth());
    }
  }, [dispatch, isInitialized]);

  // Public login routes render their own loading UI; only gate on app init
  const isPublicAuthRoute = !requireAuth;

  if (!isInitialized || (isLoading && !isPublicAuthRoute)) {
    return (
      <div
        className={isPublicAuthRoute ? 'auth-route-init-loading' : undefined}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px',
          color: isPublicAuthRoute ? '#98a0a8' : '#666',
          background: isPublicAuthRoute ? '#1a1a1a' : undefined,
        }}
      >
        Loading...
      </div>
    );
  }

  // If route requires authentication but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    // Save the attempted location for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAuth && isAuthenticated && requireOnboardingComplete) {
    return <OnboardingGuard>{children}</OnboardingGuard>;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
